import {defineStore} from 'pinia'
import {useLocalStorage} from '@vueuse/core'
import type {ContractDetail, FullContract} from '~/models/contract'
import {
    CONTRACT_TYPE_FOREST_OPERATION,
    CONTRACT_TYPE_REGISTRATION, CONTRACT_TYPE_SUPPLY_WOOD,
    MS_DELAY_CRM_TO_VH_SYNC,
    MS_INTERVAL_CLEAN_MODIFIED_TIMESTAMPS
} from "~/constants/contracts";
import {DataType, RequestType} from "~/models/queue";
import {cleanDraftContract, getHasOutdatedPeriod, isDraft} from "~/helpers/contracts";
import {useQueueDb} from "~/use/indexedDb/queueDb";
import {useQueueSizeStore} from "~/use/ui/queueSize";
import {generateUUID} from "~/helpers/uuid";
import {cloneDeep} from "lodash";
import {
    CONTRACT_STATES_SIGNED,
    CONTRACT_STATES_VH_DRAFT,
    CONTRACT_TYPES_REQUIRING_INTERNAL_NO
} from "~/constants/contract";
import {getContractCustomer} from "~/helpers/contractHelper";
import {useIndexedDBStorage} from "~/composables/useIndexedDBStorage";
import {GmModelLibraryModelsAssortmentModel} from "~/gen/openapi/sblService";
import {featureEnabled} from "~/helpers/features";
import {requiresSubType} from "~/helpers/assortmentsHelper";
import {useAssortmentService} from "~/services/assortmentService";
import {queryClient} from "~/plugins/vue-query";
import {useContractService} from "~/services/contractService";
import {useEntrepreneurService} from "~/services/entrepreneurService";
import {ProductionTypeFelling, ProductionTypeFellingAndDelivery} from "~/constants/productionOrders";
import {
    contractTypeShouldHaveEnvironmentReport,
    hasDuplicateProductionTypes,
    hasExpiredPeriods
} from "~/helpers/validations";

/**
 * @message Displayable error message
 * @path Link path the error message will lead the user to
 * @pathName Displayable name
 * @anchor Hash anchor - must include #
 */
export interface ContractActivationError {
    message: string;
    path?: string
    pathName?: string
    anchor?: string
}

export const useContractStore = defineStore('contract', () => {
    /**
     * Editable models for contracts.
     * For read-only usage of contract data or the reference contract, use data from contractService.
     *
     * Not to be confused with local drafts stored in draftsDb.
     */
    const contractDrafts = useIndexedDBStorage<Record<string, FullContract>>(
        'pinia/contracts/contractDrafts',
        {},
        {
            mergeDefaults: true,
            migrateFromLocalStorage: true,
            clearLocalStorageAfterMigration: true
        },
    )

    /**
     * When a contract last edited properties that needs syncing.
     * Activation should wait after these changes so CRM and VH
     * have a chance to catch up syncing first.
     *
     * Similar to Production Orders locking.
     * Prevents user getting errors caused by activation while sync is ongoing.
     * Should be replaced in the future™.
     * */
    const modifiedPropsThatNeedSyncingTimestamps = useLocalStorage<{ [key: string]: number }>('pinia/contracts/contractModifiedPropsThatNeedSyncingTimestamps', {} as { [key: string]: number })

    /**
     * Get a reference to the contract draft.
     * Use this as a model to write to when editing a contract.
     *
     * @param id
     * @param defaultDraft - model to add if it doesn't already exist
     * @param overWriteDraft - force the draft in store to be overwritten with defaultDraft
     */
    const getContractDraftById = (id: string, defaultDraft?: FullContract, overWriteDraft = false) => {
        if (defaultDraft && (!contractDrafts.value[id] || overWriteDraft)) {
            contractDrafts.value[id] = cloneDeep(defaultDraft)
        }

        return computed({
            get: () => contractDrafts.value[id],
            set: (val: FullContract) => contractDrafts.value[id] = val
        })
    }

    const addContractDraft = (contractDraft: FullContract) => {
        if (contractDraft.Id && !contractDrafts.value[contractDraft.Id]) {
            contractDrafts.value[contractDraft.Id] = cloneDeep(contractDraft)
        }
    }


    const contractDraftExists = (id: string) => {
        return !!contractDrafts.value[id]
    }

    /**
     * NB! Not needed when using {@link getContractDraftById} to get a model.
     * This function should only be used to overwrite the whole contract draft, i.e. resetting changes or making large changes without a user input field
     * @param updatedContractDraft
     */
    const updateContractDraft = (updatedContractDraft: FullContract) => {
        if (updatedContractDraft.Id && contractDraftExists(updatedContractDraft.Id)) {
            contractDrafts.value[updatedContractDraft.Id] = cloneDeep(updatedContractDraft)
        }
    }

    const removeContractDraftById = (id: string) => {
        if (contractDrafts.value[id]) {
            delete contractDrafts.value[id]
        }
    }

    /**
     * Cleans up outdated modifiedTimestamps
     */
    function cleanModifiedTimestamps() {
        const aLittleWhileAgo = new Date().getTime() - MS_DELAY_CRM_TO_VH_SYNC

        modifiedPropsThatNeedSyncingTimestamps.value = {
            ...Object.fromEntries(
                Object.entries(modifiedPropsThatNeedSyncingTimestamps.value).filter(
                    // Only include relevant timestamps
                    ([_, lastModifiedTimestamp]) => lastModifiedTimestamp > aLittleWhileAgo
                )
            )
        }
    }

    function touchModifiedPropsThatNeedSyncing(contractId: string, time?: number) {
        modifiedPropsThatNeedSyncingTimestamps.value = {
            ...modifiedPropsThatNeedSyncingTimestamps.value,
            [contractId]: time ?? new Date().getTime()
        }
    }

    const getActivationLockedUntil = (contractId: string) => {
        const ts = modifiedPropsThatNeedSyncingTimestamps.value[contractId]
        if (ts) {
            return ts + MS_DELAY_CRM_TO_VH_SYNC
        }
        return 0
    }

    const getUrlAndMethod = (contract: FullContract): { url: string; method: RequestType } => {
        let url: string
        let method: RequestType
        if (isDraft(contract)) {
            url = '/v1/contracts'
            method = RequestType.post
        } else {
            url = `/v1/contracts/${contract.Id}`
            method = RequestType.put
        }

        return {
            url,
            method
        }
    }

    // make sure to clean up local state to avoid cluttering localStorage too much
    setInterval(cleanModifiedTimestamps, MS_INTERVAL_CLEAN_MODIFIED_TIMESTAMPS)

    const addContractToQueue = async (contract: FullContract): Promise<void> => {
        const { url, method } = getUrlAndMethod(contract)
        const { queueDb } = useQueueDb()
        const { updateQueueSize } = useQueueSizeStore()
        const cleanedContract = isDraft(contract)
            ? cleanDraftContract(contract)
            : contract
        const customer = getContractCustomer(contract)

        // Check if contract is already in queue
        // If in queue pass id as key to `setById`
        const queue = await queueDb.getAll()
        const existing = queue.find((item) => item.payload.TempId === contract.Id)

        await queueDb.setById({
            Id: existing?.Id || generateUUID(),
            url,
            date: new Date(),
            requestType: method,
            payload: cleanedContract,
            type: DataType.Contract,
            title: customer?.Name!,
        }).then(async () => {
            const contractService = useContractService()
            contractService.updateContractInContractList(contract)
            await queryClient.invalidateQueries({queryKey: ['contractGetContract', existing?.Id], refetchType: "none"})
            await queryClient.invalidateQueries({queryKey: ['contractGetContractList'], refetchType: "none"})
        })

        await updateQueueSize()
    }

    const activationValidationErrors = (contract?: Ref<FullContract>, contractReference?: Ref<FullContract>): ComputedRef<ContractActivationError[]> => {
        return computed(() => {
            const { getEntrepreneurById } = useEntrepreneurService()
            const runtimeConfig = useRuntimeConfig()
            const errors: Array<ContractActivationError> = []
            if (!contract?.value || !contractReference?.value) {
                return errors
            }
            const currentContractHasOutdatedPeriod = getHasOutdatedPeriod(contract.value)
            const contractReferenceHasOutdatedPeriod = getHasOutdatedPeriod(contractReference.value)
            const { getAssortmentsQueryOptions } = useAssortmentService()
            const assortments = queryClient.getQueryData(getAssortmentsQueryOptions.queryKey) as Array<GmModelLibraryModelsAssortmentModel>

            if (![CONTRACT_STATES_SIGNED, CONTRACT_STATES_VH_DRAFT].includes(contract.value.Status!)) {
                errors.push({message: 'ACTIVATION_ERROR_CONTRACT_NOT_READY'})
            }
            if (!contract.value.VsysNo && featureEnabled(runtimeConfig.public.FEATURE_REQUIRE_VSYS_ID_FOR_CONTRACT_ACTIVATION)) {
                errors.push({message: 'ACTIVATION_ERROR_MISSING_VSYS_NO'})
            }
            if (contractReferenceHasOutdatedPeriod && currentContractHasOutdatedPeriod) {
                errors.push({message: 'ACTIVATION_ERROR_OUTDATED_PERIOD'})
            }
            if (!contract.value.Details?.length) {
                errors.push({message: 'ACTIVATION_ERROR_MUST_HAVE_ASSORTMENTS', anchor:'#contractDetailsSection', pathName: 'Legg til'})
            }
            if (Array.isArray(contract.value.Details) && contract.value.Details.some((detail) => !detail.TimberPriceListId)) {
                errors.push({message:'ACTIVATION_ERROR_MUST_HAVE_PRICELISTS', anchor:'#contractDetailsSection', pathName: 'Legg til'})
            }
            if (!contract.value.TopplePlaces || !contract.value.TopplePlaces.length) {
                errors.push({message: 'ACTIVATION_ERROR_MUST_HAVE_TOPPLE_PLACE', anchor:'#topplePlacesSection', pathName: 'Legg til'})
            }
            if (!contract.value.UseLargeForestOwnerFlow && (!contract.value.ProductionOrders?.length || !contract.value.ProductionOrders.some((po) => po.ProductionTypeCode && [ProductionTypeFelling, ProductionTypeFellingAndDelivery].includes(po.ProductionTypeCode)))) {
                errors.push({message: 'ACTIVATION_ERROR_MISSING_ENTREPRENEUR', anchor:'#productionOrdersSection', pathName: 'Legg til'})
            }
            if (contract.value.UseLargeForestOwnerFlow) {
                if (!contract.value.ProductionOrders?.every((order) => order.Entrepreneur?.Id)) {
                  errors.push({message: 'LARGE_FOREST_OWNER_FLOW_ENTREPRENEUR_MISSING'})
                }
                if (!contract.value.EnvironmentReport?.SignedEnvironmentalReportForestOwner) {
                    errors.push({message: 'LARGE_FOREST_OWNER_FLOW_ENVIRONMENT_REPORT_NOT_SIGNED'})
                }
            } else if (contractTypeShouldHaveEnvironmentReport(contract.value.Type!) && !contract.value.EnvironmentReport?.SignedForestryManager) {
                errors.push({message: 'ENVIRONMENTAL_REPORT_MUST_BE_FINISHED', path: `/customers/${contract.value.ForestOwnerId}/${contract.value.PropertyId}/${contract.value.Id}/environmentReport`, pathName: 'Ferdigstill'})
            }

            if (contract.value.Details?.some((detail: ContractDetail) => {
                return requiresSubType(detail, toRef(assortments)) && !detail.WoodSpecies?.length
            })) {
                errors.push({message: 'ACTIVATION_ERROR_MISSING_ASSORTMENTS_SUB_TYPE'})
            }

            if (contract.value.AssortmentInformation?.EntrepreneurId) {
                const entrepreneur = computed(() => getEntrepreneurById(contract.value.AssortmentInformation?.EntrepreneurId!))
                if (contract.value.Type && [CONTRACT_TYPE_REGISTRATION, CONTRACT_TYPE_SUPPLY_WOOD, CONTRACT_TYPE_FOREST_OPERATION].includes(contract.value.Type) && !entrepreneur.value?.VsysNo) {
                    errors.push({message: 'ENTREPRENEUR_MISSING_VSYS_NO'})
                }
            }

            if (hasDuplicateProductionTypes(contract.value.ProductionOrders)) {
                errors.push({message: 'DUPLICATE_PRODUCTION_TYPES_ERROR'})
            }

            if (contract.value.Details && hasExpiredPeriods(contract.value.Details)) {
                errors.push({message: 'ACTIVATION_ERROR_ASSORTMENTS_HAS_EXPIRED_PERIODS'})
            }

            if (CONTRACT_TYPES_REQUIRING_INTERNAL_NO.includes(contract.value.Type!) && !contract.value.InternalNo) {
                errors.push({message: 'ACTIVATION_ERROR_MISSING_INTERNAL_NO'})
            }

            return errors
        })
    }

    return {
        contractDrafts,
        modifiedPropsThatNeedSyncingTimestamps,
        getActivationLockedUntil,
        activationValidationErrors,
        getContractDraftById,
        contractDraftExists,
        addContractDraft,
        updateContractDraft,
        removeContractDraftById,
        touchModifiedPropsThatNeedSyncing,
        addContractToQueue,
    }
})